// =====================================================================
// ADMIN · FACTURACIÓN (módulo independiente)
//
// Centro de control de CFDIs:
//   - Facturas emitidas (timbradas, válidas en el SAT)
//   - Borradores (drafts pendientes de timbrar)
//   - Canceladas (CFDIs cancelados)
//
// Filtros: búsqueda libre, cliente, pedido, rango de fechas, serie/folio.
// Acciones: nueva factura (desde pedido), abrir draft, ver PDF/XML, copiar UUID.
// =====================================================================

import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { AdminContainer } from '../AdminShell';
import { InvoiceDraftModal } from './InvoiceDraftModal';

type Tab = 'emitidas' | 'borradores' | 'canceladas';

// ----------- Tipos -----------
type InvoiceRow = {
  id: string;
  invoice_number: string;
  order_id: string | null;
  customer_id: string;
  uuid_fiscal: string | null;
  serie: string | null;
  folio: string | null;
  tipo_comprobante: string | null;
  fecha_emision: string | null;
  fecha_timbrado: string | null;
  receptor_rfc: string | null;
  receptor_nombre: string | null;
  subtotal: number | null;
  iva: number | null;
  total: number | null;
  moneda: string | null;
  metodo_pago: string | null;
  forma_pago: string | null;
  xml_url: string | null;
  pdf_url: string | null;
  estado: string;
  sandbox: boolean;
  created_at: string;
  // joined
  customer_name?: string;
  order_number?: string;
};

type DraftRow = {
  id: string;
  order_id: string;
  customer_id: string;
  status: string;
  stamping_error: string | null;
  receptor_rfc: string;
  receptor_legal_name: string;
  moneda: string;
  total: number;
  created_at: string;
  updated_at: string;
  // joined
  customer_name?: string;
  order_number?: string;
};

type Customer = { id: string; business_name: string };
type Order = { id: string; order_number: string | null; customer_id: string | null; total: number | null; currency: string };

// =====================================================================
// MAIN
// =====================================================================
export function AdminFacturacion() {
  const [tab, setTab] = useState<Tab>('emitidas');
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [drafts, setDrafts] = useState<DraftRow[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);

  // Filtros
  const [q, setQ] = useState('');
  const [customerFilter, setCustomerFilter] = useState<string>('');
  const [orderFilter, setOrderFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // Modales
  const [draftId, setDraftId] = useState<string | null>(null);
  const [newInvoicePicker, setNewInvoicePicker] = useState(false);

  // ----- Load data -----
  async function load() {
    setLoading(true);
    const [invRes, drRes, custRes] = await Promise.all([
      supabase
        .from('customer_invoices')
        .select(`
          id, invoice_number, order_id, customer_id, uuid_fiscal, serie, folio,
          tipo_comprobante, fecha_emision, fecha_timbrado,
          receptor_rfc, receptor_nombre,
          subtotal, iva, total, moneda, metodo_pago, forma_pago,
          xml_url, pdf_url, estado, sandbox, created_at,
          customers ( business_name ),
          orders ( order_number )
        `)
        .order('fecha_emision', { ascending: false })
        .limit(500),
      supabase
        .from('customer_invoice_drafts')
        .select(`
          id, order_id, customer_id, status, stamping_error,
          receptor_rfc, receptor_legal_name, moneda, total,
          created_at, updated_at,
          customers ( business_name ),
          orders ( order_number )
        `)
        .in('status', ['draft', 'stamping', 'failed'])
        .order('updated_at', { ascending: false }),
      supabase.from('customers').select('id, business_name').order('business_name'),
    ]);

    setInvoices(((invRes.data as any[]) ?? []).map(r => ({
      ...r,
      customer_name: r.customers?.business_name,
      order_number: r.orders?.order_number,
    })));
    setDrafts(((drRes.data as any[]) ?? []).map(r => ({
      ...r,
      customer_name: r.customers?.business_name,
      order_number: r.orders?.order_number,
    })));
    setCustomers((custRes.data as Customer[]) ?? []);
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  // ----- Counts -----
  const counts = useMemo(() => ({
    emitidas: invoices.filter(i => i.estado === 'active').length,
    borradores: drafts.length,
    canceladas: invoices.filter(i => i.estado === 'canceled').length,
  }), [invoices, drafts]);

  // ----- Filter -----
  function matchesInvoice(i: InvoiceRow): boolean {
    if (customerFilter && i.customer_id !== customerFilter) return false;
    if (orderFilter && i.order_id !== orderFilter) return false;
    if (dateFrom && i.fecha_emision && i.fecha_emision < dateFrom) return false;
    if (dateTo && i.fecha_emision && i.fecha_emision > dateTo + 'T23:59:59') return false;
    if (q) {
      const qn = q.toLowerCase();
      const hay = [
        i.invoice_number, i.uuid_fiscal, i.serie, i.folio,
        i.receptor_rfc, i.receptor_nombre, i.customer_name, i.order_number
      ].filter(Boolean).map(s => (s ?? '').toLowerCase()).join(' ');
      if (!hay.includes(qn)) return false;
    }
    return true;
  }

  function matchesDraft(d: DraftRow): boolean {
    if (customerFilter && d.customer_id !== customerFilter) return false;
    if (orderFilter && d.order_id !== orderFilter) return false;
    if (q) {
      const qn = q.toLowerCase();
      const hay = [d.receptor_rfc, d.receptor_legal_name, d.customer_name, d.order_number]
        .filter(Boolean).map(s => (s ?? '').toLowerCase()).join(' ');
      if (!hay.includes(qn)) return false;
    }
    return true;
  }

  const filteredEmitidas = invoices.filter(i => i.estado === 'active' && matchesInvoice(i));
  const filteredCanceladas = invoices.filter(i => i.estado === 'canceled' && matchesInvoice(i));
  const filteredDrafts = drafts.filter(matchesDraft);

  function clearFilters() {
    setQ(''); setCustomerFilter(''); setOrderFilter(''); setDateFrom(''); setDateTo('');
  }

  // ----- Render -----
  return (
    <AdminContainer
      title="Facturación"
      eyebrow="CFDI 4.0 · Facturapi"
      action={
        <button onClick={() => setNewInvoicePicker(true)} style={btnPrimary}>
          + Nueva factura
        </button>
      }
    >
      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${theme.border}`, marginBottom: 16 }}>
        <TabBtn active={tab === 'emitidas'} onClick={() => setTab('emitidas')}>
          Emitidas <span style={{ opacity: 0.6 }}>({counts.emitidas})</span>
        </TabBtn>
        <TabBtn active={tab === 'borradores'} onClick={() => setTab('borradores')}>
          Borradores <span style={{ opacity: 0.6 }}>({counts.borradores})</span>
        </TabBtn>
        <TabBtn active={tab === 'canceladas'} onClick={() => setTab('canceladas')}>
          Canceladas <span style={{ opacity: 0.6 }}>({counts.canceladas})</span>
        </TabBtn>
      </div>

      {/* Filtros */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: 8, marginBottom: 16 }}>
        <input
          placeholder="Buscar folio, UUID, RFC, cliente, pedido…"
          value={q} onChange={e => setQ(e.target.value)}
          style={inputStyle}
        />
        <select value={customerFilter} onChange={e => setCustomerFilter(e.target.value)} style={inputStyle}>
          <option value="">Todos los clientes</option>
          {customers.map(c => (
            <option key={c.id} value={c.id}>{c.business_name}</option>
          ))}
        </select>
        <input
          type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          style={inputStyle} title="Desde"
        />
        <input
          type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          style={inputStyle} title="Hasta"
        />
        {(q || customerFilter || orderFilter || dateFrom || dateTo) && (
          <button onClick={clearFilters} style={btnSecondary}>Limpiar</button>
        )}
      </div>

      <div style={{ marginBottom: 8, fontSize: 12, color: theme.textMuted }}>
        {loading ? 'Cargando…' :
          tab === 'emitidas'   ? `${filteredEmitidas.length} de ${counts.emitidas}` :
          tab === 'borradores' ? `${filteredDrafts.length} de ${counts.borradores}` :
                                  `${filteredCanceladas.length} de ${counts.canceladas}`
        }
      </div>

      {/* Tablas */}
      {tab === 'emitidas' && (
        <InvoicesTable rows={filteredEmitidas} onOpen={() => {}} />
      )}
      {tab === 'borradores' && (
        <DraftsTable rows={filteredDrafts} onOpen={id => setDraftId(id)} onReload={load} />
      )}
      {tab === 'canceladas' && (
        <InvoicesTable rows={filteredCanceladas} onOpen={() => {}} isCanceled />
      )}

      {/* Modal: editar/timbrar draft */}
      {draftId && (
        <InvoiceDraftModal
          draftId={draftId}
          onClose={() => setDraftId(null)}
          onStamped={async () => { setDraftId(null); await load(); }}
        />
      )}

      {/* Modal: seleccionar pedido para nueva factura */}
      {newInvoicePicker && (
        <NewInvoicePicker
          onClose={() => setNewInvoicePicker(false)}
          onDraftCreated={id => { setNewInvoicePicker(false); setDraftId(id); }}
        />
      )}
    </AdminContainer>
  );
}

// =====================================================================
// TABLA: facturas emitidas / canceladas
// =====================================================================
function InvoicesTable({ rows, isCanceled }: {
  rows: InvoiceRow[];
  onOpen: (id: string) => void;
  isCanceled?: boolean;
}) {
  if (rows.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted, border: `1px dashed ${theme.border}` }}>
        Sin facturas {isCanceled ? 'canceladas' : 'emitidas'} con los filtros aplicados.
      </div>
    );
  }
  return (
    <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: theme.bgSoft }}>
            <Th>Folio</Th>
            <Th>Fecha</Th>
            <Th>Cliente · RFC</Th>
            <Th>Pedido</Th>
            <Th>Tipo</Th>
            <Th style={{ textAlign: 'right' }}>Total</Th>
            <Th>Método</Th>
            <Th>UUID</Th>
            <Th style={{ textAlign: 'right' }}>Acciones</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} style={{ borderBottom: `1px solid ${theme.border}`, opacity: isCanceled ? 0.6 : 1 }}>
              <Td>
                <div style={{ fontFamily: theme.font.mono, fontWeight: 700, color: theme.brand }}>
                  {r.invoice_number}
                </div>
                {r.sandbox && <Tag color="#9A7B00">TEST</Tag>}
              </Td>
              <Td style={{ fontSize: 11 }}>
                {r.fecha_emision ? new Date(r.fecha_emision).toLocaleDateString('es-MX') : '—'}
              </Td>
              <Td>
                <div style={{ fontWeight: 600 }}>{r.customer_name ?? '—'}</div>
                <div style={{ fontSize: 10, color: theme.textMuted, fontFamily: theme.font.mono }}>{r.receptor_rfc}</div>
              </Td>
              <Td style={{ fontSize: 11, color: theme.textMuted }}>{r.order_number ?? '—'}</Td>
              <Td>
                <Tag color={r.tipo_comprobante === 'I' ? '#15803D' : r.tipo_comprobante === 'E' ? '#B91C1C' : theme.textMuted}>
                  {tipoComprobanteLabel(r.tipo_comprobante)}
                </Tag>
              </Td>
              <Td style={{ textAlign: 'right', fontWeight: 700 }}>
                ${Number(r.total ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} {r.moneda}
              </Td>
              <Td style={{ fontSize: 11 }}>{r.metodo_pago ?? '—'} · {r.forma_pago ?? '—'}</Td>
              <Td>
                {r.uuid_fiscal ? (
                  <button onClick={() => copyText(r.uuid_fiscal!)} style={uuidBtn} title="Click para copiar UUID">
                    {r.uuid_fiscal.slice(0, 8)}…{r.uuid_fiscal.slice(-4)}
                  </button>
                ) : '—'}
              </Td>
              <Td style={{ textAlign: 'right' }}>
                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                  <DownloadBtn invoiceId={r.id} format="pdf" />
                  <DownloadBtn invoiceId={r.id} format="xml" />
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =====================================================================
// TABLA: borradores
// =====================================================================
function DraftsTable({ rows, onOpen, onReload }: {
  rows: DraftRow[];
  onOpen: (id: string) => void;
  onReload: () => void;
}) {
  if (rows.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted, border: `1px dashed ${theme.border}` }}>
        Sin borradores pendientes. Las facturas en proceso aparecerán aquí.
      </div>
    );
  }

  async function discard(id: string) {
    if (!confirm('¿Descartar este borrador? No se podrá recuperar.')) return;
    const { error } = await supabase.from('customer_invoice_drafts').delete().eq('id', id);
    if (error) { alert(`Error: ${error.message}`); return; }
    onReload();
  }

  return (
    <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: theme.bgSoft }}>
            <Th>Cliente · RFC</Th>
            <Th>Pedido</Th>
            <Th>Estatus</Th>
            <Th style={{ textAlign: 'right' }}>Total</Th>
            <Th>Última edición</Th>
            <Th style={{ textAlign: 'right' }}>Acciones</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
              <Td>
                <div style={{ fontWeight: 600 }}>{r.customer_name ?? '—'}</div>
                <div style={{ fontSize: 10, color: theme.textMuted, fontFamily: theme.font.mono }}>{r.receptor_rfc}</div>
                <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 2 }}>{r.receptor_legal_name}</div>
              </Td>
              <Td style={{ fontSize: 11, color: theme.textMuted }}>{r.order_number ?? '—'}</Td>
              <Td>
                <DraftStatusTag status={r.status} />
                {r.stamping_error && (
                  <div style={{ fontSize: 10, color: '#B91C1C', marginTop: 4, maxWidth: 280, lineHeight: 1.3 }}>
                    {r.stamping_error.slice(0, 140)}{r.stamping_error.length > 140 ? '…' : ''}
                  </div>
                )}
              </Td>
              <Td style={{ textAlign: 'right', fontWeight: 700 }}>
                ${Number(r.total ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} {r.moneda}
              </Td>
              <Td style={{ fontSize: 11, color: theme.textMuted }}>
                {new Date(r.updated_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
              </Td>
              <Td style={{ textAlign: 'right' }}>
                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                  <button onClick={() => onOpen(r.id)} style={btnXs}>Abrir</button>
                  <button onClick={() => discard(r.id)} style={{ ...btnXs, color: '#B91C1C', borderColor: '#B91C1C' }}>Descartar</button>
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =====================================================================
// MODAL: selector de pedido para nueva factura
// =====================================================================
function NewInvoicePicker({ onClose, onDraftCreated }: {
  onClose: () => void;
  onDraftCreated: (draftId: string) => void;
}) {
  const [orders, setOrders] = useState<(Order & { customer_name?: string })[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      // Solo pedidos sin factura activa, recientes primero.
      const { data } = await supabase
        .from('orders')
        .select(`
          id, order_number, customer_id, total, currency, status,
          customers ( business_name )
        `)
        .in('status', ['confirmed', 'paid', 'shipped', 'delivered'])
        .order('created_at', { ascending: false })
        .limit(200);

      // Filtrar los que ya tienen factura activa
      const orderIds = (data ?? []).map((o: any) => o.id);
      const { data: invoiced } = await supabase
        .from('customer_invoices')
        .select('order_id')
        .in('order_id', orderIds)
        .eq('estado', 'active');
      const invoicedIds = new Set((invoiced ?? []).map((i: any) => i.order_id));

      setOrders(((data as any[]) ?? [])
        .filter(o => !invoicedIds.has(o.id))
        .map(o => ({
          id: o.id, order_number: o.order_number, customer_id: o.customer_id,
          total: o.total, currency: o.currency,
          customer_name: o.customers?.business_name,
        })));
      setLoading(false);
    })();
  }, []);

  async function createDraft(orderId: string) {
    setCreating(true);
    setErr(null);
    const { data, error } = await supabase.rpc('prepare_invoice_draft', { p_order_id: orderId });
    setCreating(false);
    if (error) { setErr(error.message); return; }
    if (!data) { setErr('No se pudo crear el borrador'); return; }
    onDraftCreated(data as string);
  }

  const filtered = orders.filter(o => {
    if (!q) return true;
    const qn = q.toLowerCase();
    return (o.order_number ?? '').toLowerCase().includes(qn) ||
           (o.customer_name ?? '').toLowerCase().includes(qn);
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: theme.bg, border: `1px solid ${theme.border}`,
        width: '100%', maxWidth: 720, maxHeight: '85vh', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${theme.border}` }}>
          <div style={{ fontSize: 11, color: theme.accentDark, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700 }}>
            Nueva factura
          </div>
          <h2 style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 400, color: theme.brand, margin: '4px 0 12px' }}>
            Selecciona el pedido a facturar
          </h2>
          <input
            placeholder="Buscar pedido o cliente…"
            value={q} onChange={e => setQ(e.target.value)}
            style={inputStyle} autoFocus
          />
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted }}>Cargando pedidos…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted }}>
              {orders.length === 0
                ? 'No hay pedidos sin facturar. Todos los pedidos confirmados ya tienen CFDI emitido.'
                : 'Sin pedidos que coincidan con la búsqueda.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {filtered.map(o => (
                <button key={o.id} onClick={() => createDraft(o.id)} disabled={creating}
                  style={{
                    display: 'grid', gridTemplateColumns: '120px 1fr auto', gap: 12,
                    padding: '12px 14px', background: theme.bgSoft,
                    border: `1px solid ${theme.border}`,
                    textAlign: 'left', cursor: creating ? 'wait' : 'pointer',
                    opacity: creating ? 0.5 : 1, fontFamily: theme.font.sans,
                    color: theme.brand,
                  }}>
                  <div style={{ fontFamily: theme.font.mono, fontWeight: 700, fontSize: 12 }}>
                    {o.order_number ?? o.id.slice(0, 8)}
                  </div>
                  <div style={{ fontSize: 12 }}>{o.customer_name ?? '—'}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, textAlign: 'right' }}>
                    ${Number(o.total ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} {o.currency}
                  </div>
                </button>
              ))}
            </div>
          )}
          {err && (
            <div style={{ marginTop: 12, padding: 10, background: 'rgba(220,38,38,0.08)', border: `1px solid #B91C1C`, color: '#B91C1C', fontSize: 12 }}>
              {err}
            </div>
          )}
        </div>
        <div style={{ padding: '14px 24px', borderTop: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btnSecondary}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// DOWNLOAD BUTTON (proxy a Facturapi con auth)
//
// Los URLs directos de Facturapi requieren Basic Auth con el API key.
// Si el browser abre el link directo, muestra prompt user/password.
// Por eso pasamos por edge function facturapi-download-cfdi que descarga
// con la key y nos devuelve el archivo binario, que abrimos como blob URL.
// =====================================================================
function DownloadBtn({ invoiceId, format }: { invoiceId: string; format: 'pdf' | 'xml' }) {
  const [busy, setBusy] = useState(false);

  async function download() {
    setBusy(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        alert('Sesión expirada. Inicia sesión de nuevo.');
        setBusy(false);
        return;
      }
      // Llamada directa con fetch (functions.invoke no soporta respuesta binaria)
      const supabaseUrl = (supabase as any).supabaseUrl || (supabase as any).rest?.url?.replace('/rest/v1', '');
      const fnUrl = `${supabaseUrl}/functions/v1/facturapi-download-cfdi`;
      const res = await fetch(fnUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ invoice_id: invoiceId, format }),
      });
      if (!res.ok) {
        let msg = `Error ${res.status}`;
        try { const j = await res.json(); msg = j.error || msg; } catch { /* ignore */ }
        alert(`No se pudo descargar el ${format.toUpperCase()}: ${msg}`);
        setBusy(false);
        return;
      }
      // Convertir respuesta a blob y forzar download
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Extraer filename del Content-Disposition si vino
      const disp = res.headers.get('Content-Disposition') || '';
      const m = disp.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : `cfdi.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Liberar después de 1s para que el browser termine la descarga
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert(`Error: ${(e as Error).message}`);
    }
    setBusy(false);
  }

  return (
    <button onClick={download} disabled={busy} style={{ ...btnXs, opacity: busy ? 0.5 : 1, cursor: busy ? 'wait' : 'pointer' }}>
      {busy ? '…' : format.toUpperCase()}
    </button>
  );
}

// =====================================================================
// Helpers UI
// =====================================================================
function copyText(s: string) {
  navigator.clipboard.writeText(s).then(() => {/* silently ok */}, () => {/* ignore */});
}

function tipoComprobanteLabel(t: string | null): string {
  switch (t) {
    case 'I': return 'Ingreso';
    case 'E': return 'Egreso (NC)';
    case 'P': return 'Pago';
    case 'N': return 'Nómina';
    case 'T': return 'Traslado';
    default: return t ?? '—';
  }
}

function Th({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <th style={{
      textAlign: 'left', padding: '10px 12px', fontSize: 10, fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: 0.5, color: theme.textMuted,
      borderBottom: `1px solid ${theme.border}`, ...style,
    }}>{children}</th>
  );
}

function Td({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <td style={{ padding: '10px 12px', verticalAlign: 'top', ...style }}>{children}</td>;
}

function Tag({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <span style={{
      display: 'inline-block', fontSize: 10, fontWeight: 700, padding: '2px 7px',
      color, border: `1px solid ${color}`, textTransform: 'uppercase', letterSpacing: 0.5,
    }}>{children}</span>
  );
}

function DraftStatusTag({ status }: { status: string }) {
  const colors: Record<string, string> = {
    draft: theme.textMuted,
    stamping: '#9A7B00',
    failed: '#B91C1C',
    stamped: '#15803D',
    discarded: theme.textMuted,
  };
  const labels: Record<string, string> = {
    draft: 'Borrador',
    stamping: 'Timbrando…',
    failed: 'Error',
    stamped: 'Timbrado',
    discarded: 'Descartado',
  };
  return <Tag color={colors[status] ?? theme.textMuted}>{labels[status] ?? status}</Tag>;
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: 'transparent', color: active ? theme.brand : theme.textMuted,
      border: 'none', borderBottom: `2px solid ${active ? theme.accent : 'transparent'}`,
      padding: '12px 18px', fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
      cursor: 'pointer', fontFamily: theme.font.sans, marginBottom: -1,
    }}>{children}</button>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', background: theme.bg, border: `1px solid ${theme.border}`,
  padding: '10px 12px', color: theme.text, fontSize: 13,
  fontFamily: theme.font.sans, outline: 'none', boxSizing: 'border-box',
};

const btnPrimary: React.CSSProperties = {
  background: theme.accent, color: theme.brand, border: 'none',
  padding: '10px 18px', fontSize: 12, fontWeight: 700, letterSpacing: 0.4,
  textTransform: 'uppercase', cursor: 'pointer', fontFamily: theme.font.sans,
};

const btnSecondary: React.CSSProperties = {
  background: 'transparent', border: `1px solid ${theme.border}`,
  color: theme.brand, padding: '10px 14px',
  fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
  cursor: 'pointer', fontFamily: theme.font.sans,
};

const btnXs: React.CSSProperties = {
  background: 'transparent', border: `1px solid ${theme.border}`,
  color: theme.brand, padding: '5px 10px',
  fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
  cursor: 'pointer', fontFamily: theme.font.sans,
};

const uuidBtn: React.CSSProperties = {
  background: 'transparent', border: 'none', color: theme.textMuted,
  fontFamily: theme.font.mono, fontSize: 10, cursor: 'pointer', padding: 0,
  textDecoration: 'underline',
};
